/**
 * Pure helpers that turn session/instance data already on the wire into the props `BoardTransition.tsx`
 * wants (docs/concept/board/06-minimal-transition.png: "stage, title, format, players, teams, readiness,
 * countdown value" — this module builds everything except stage/countdown, which come from
 * `useBoardTransitionStage`). Kept separate from the reducer so both are independently unit-testable
 * without a DOM.
 */
import type { CharacterSlotId, DeadlinePublic, ParticipantPublic, TeamAssignment } from '@momentum/shared';

export interface TransitionPlayer {
  id: string;
  name: string;
  teamId: string | null;
  characterSlot: CharacterSlotId | null;
  ready: boolean;
  disconnected: boolean;
}

export interface TransitionTeam {
  id: string;
  label: string;
}

/**
 * Orders players by team (when teams exist) and fills in what the transition screen needs to show for each:
 * display name, colour slot, whether they're in `readyIds`, and whether their connection has dropped (the
 * "disconnect recovery must stay explicit" requirement — the caller surfaces this, not a silent hang).
 */
export function buildTransitionPlayers(
  participantIds: string[],
  teams: TeamAssignment[] | null,
  participants: ParticipantPublic[],
  readyIds: ReadonlySet<string>,
): TransitionPlayer[] {
  const byId = new Map(participants.map((p) => [p.id, p]));
  const teamOf = new Map<string, string>();
  for (const team of teams ?? []) {
    for (const id of team.participantIds) teamOf.set(id, team.teamId);
  }
  const orderedIds = teams && teams.length > 0 ? teams.flatMap((t) => t.participantIds) : participantIds;

  return orderedIds.map((id) => {
    const participant = byId.get(id);
    return {
      id,
      name: participant?.displayName ?? id,
      teamId: teamOf.get(id) ?? null,
      characterSlot: participant?.characterSlot ?? null,
      ready: readyIds.has(id),
      disconnected: participant ? participant.connection !== 'connected' : false,
    };
  });
}

export function buildTransitionTeams(teams: TeamAssignment[] | null): TransitionTeam[] {
  return (teams ?? []).map((t) => ({ id: t.teamId, label: t.label }));
}

/**
 * "2 vs 2 · About 2 minutes" style copy from real data: team sizes (only when there are exactly two teams —
 * anything else falls back to a plain player count) and the longest live deadline, rounded up to whole
 * minutes. Never invents a duration nobody set.
 */
export function describeTransitionFormat(
  teams: TeamAssignment[] | null,
  participantCount: number,
  deadlines: DeadlinePublic[],
  serverNow: number,
): string {
  const teamPart =
    teams && teams.length === 2
      ? `${teams[0]!.participantIds.length} vs ${teams[1]!.participantIds.length}`
      : `${participantCount} player${participantCount === 1 ? '' : 's'}`;

  const longestMs = deadlines.reduce((max, deadline) => {
    const remaining = deadline.mode === 'running' ? deadline.at - serverNow : deadline.remainingMs;
    return Math.max(max, remaining);
  }, 0);

  if (longestMs <= 0) return teamPart;
  const minutes = Math.max(1, Math.round(longestMs / 60_000));
  return `${teamPart} · About ${minutes} minute${minutes === 1 ? '' : 's'}`;
}
